/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import { Form } from 'react-form';
import moment from 'moment-timezone';
import { find, cloneDeep } from 'lodash';

import Popup from '../../Popup';
import { Select, DateTimePicker } from '../../Form';
import { required } from '../../../utils/validation';
import { SupportLink } from '../../Links';

import styles from './editDualMatchEventPopup.scss';

const _ = { find, cloneDeep };

class EditDualMatchEventPopup extends Component {
    constructor(props) {
        super(props);

        const timezone = props.defaultValues.timezone || 77;
        const timezoneObj = props.coreUI.types.timeZones.find(tz => { return tz.id === parseInt(timezone); });

        this.state = {
            dualMatchEventScheduleStartDate: moment(props.defaultValues.eventStartUtc).tz(timezoneObj.value),
            dualMatchEventInitialStartDate: moment(props.defaultValues.eventStartUtc).tz(timezoneObj.value),
            showTimezone: !!props.themeV3,
            timezone,
            submitting: false,
            submitSuccessful: false,
            message: '',
        };

        this.handleHomeTeamChange = this.handleHomeTeamChange.bind(this);
        this.handleDualMatchEventStartDateChange = this.handleDualMatchEventStartDateChange.bind(this);
        this.handleDualMatchLocationChange = this.handleDualMatchLocationChange.bind(this);
        this.toggleTimezone = this.toggleTimezone.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    handleHomeTeamChange(currentEventTeam, opponentEventTeam, currentEventTeamIsHome, opponentEventTeamIsHome, formApi) {
        const currentEventTeamName = currentEventTeam.name;
        const opponentEventTeamName = opponentEventTeam.name;
        const dualMatchClubs = [{
            clubId: currentEventTeam.clubId,
            isHome: currentEventTeamIsHome,
        }, {
            clubId: opponentEventTeam.clubId,
            isHome: opponentEventTeamIsHome,
        }];

        formApi.setValue('dualMatchClubs', dualMatchClubs);

        if (currentEventTeamIsHome) {
            formApi.setValue('name', `${opponentEventTeamName} at ${currentEventTeamName} `);
        } else if (opponentEventTeamIsHome) {
            formApi.setValue('name', `${currentEventTeamName} at ${opponentEventTeamName} `);
        } else {
            formApi.setValue('name', `${opponentEventTeamName} vs. ${currentEventTeamName} `);
        }
    }

    handleDualMatchEventStartDateChange(date, formApi) {
        const eventInitialDate = this.state.dualMatchEventInitialStartDate;
        let isDateUpdated = true;

        if (this.props.club.tierType.isHighSchool) {
            const isDateOutOfRange = date < moment().add('months', -3).add('days', -1);
            if (!isDateOutOfRange) {
                this.setState({
                    message: '',
                });
            } else {
                isDateUpdated = false;
                formApi.setValue('eventStartUtc', eventInitialDate);
                this.setState({
                    message: `The event date will remain unchanged if a date before ${moment().add('months', -3).format('MMM Do YYYY')} is selected.`,
                });
            }
        }

        if (isDateUpdated) {
            this.setState({
                dualMatchEventScheduleStartDate: date,
            });
        } else {
            this.setState({
                dualMatchEventScheduleStartDate: eventInitialDate,
            });
        }
    }

    handleDualMatchLocationChange(optionValue, e, formApi) {
        const selectedMatchLocationOption = this.props.dualMatchLocationOptions.find((option) => {
            return option.value === optionValue;
        });
        const clubIdOfLocation = selectedMatchLocationOption.clubId;
        const currentEventTeam = _.find(this.props.eventTeams, { clubId: this.props.club.id });
        const opponentEventTeam = _.find(this.props.eventTeams, (val) => { return val.clubId !== currentEventTeam.clubId; });
        const currentEventTeamIsHome = clubIdOfLocation === currentEventTeam.clubId;
        const opponentEventTeamIsHome = clubIdOfLocation === opponentEventTeam.clubId;
        this.handleHomeTeamChange(currentEventTeam, opponentEventTeam, currentEventTeamIsHome, opponentEventTeamIsHome, formApi);
    }

    toggleTimezone(show, val) {
        this.setState(prevState => ({
            showTimezone: show,
            timezone: val ? parseInt(val) : prevState.timezone,
        }));
    }

    async handleSubmit(eventId, values) {
        this.setState({
            submitting: true,
            submitSuccessful: false,
            updateError: false,
        });

        try {
            await this.props.onCtaClickAsync(values);
            this.setState({
                submitting: false,
                submitSuccessful: true,
            });
        } catch (error) {
            this.setState({
                submitting: false,
                updateError: true,
            });
        }
    }

    render() {
        const {
            defaultValues,
            onCloseClick,
            coreUI,
            themeV3,
        } = this.props;

        const { timeZones } = _.cloneDeep(coreUI.types);
        timeZones.sort((tz1, tz2) => {
            const offset1 = moment().utcOffset(tz1.offset)._offset;
            const offset2 = moment().utcOffset(tz2.offset)._offset;
            if (offset1 !== offset2) {
                if (offset1 < offset2) { return -1; }
                if (offset1 > offset2) { return 1; }
            } else {
                const label1 = tz1?.label?.toUpperCase();
                const label2 = tz2?.label?.toUpperCase();
                if (label1 < label2) {
                    return -1;
                }
                if (label1 > label2) {
                    return 1;
                }
                return 0;
            }
            return 0;
        });
        timeZones.map((timezone) => {
            timezone.label = `${timezone.label} (GMT${timezone.offset})`;
            return timezone;
        });

        return (
            <Popup
                className={`popup-md clear-over-flow`}
                popUpClassName={`${styles.editDualMatchEventPopup} ${themeV3 ? styles.themeV3 : ''}`}
                onCloseClick={onCloseClick}
                data-testid="modal.edit-dual-match"
                config={{
                    noPadding: true,
                    fullScreen: false,
                    topGradient: false,
                    scrollable: false,
                }}
            >
                <div className={`${styles.section} pt0`}>
                    <h5 data-testid="modal.edit-dual-match.h5-179">{`Edit Date and Location`}</h5>
                </div>
                <div className={`${styles.section} pt0`}>
                    <Form onSubmit={values => this.handleSubmit(this.props.eventId, values)} id="editDualMatchEventForm" defaultValues={defaultValues}>
                        {(formApi) => {
                            const { submitForm, errors } = formApi;
                            return (
                                <form onSubmit={submitForm} id="editDualMatchEventForm" data-testid="modal.edit-dual-match.form-186">
                                    {!this.state.submitSuccessful && (
                                        <React.Fragment>
                                            <div className="row mb16">
                                                <div className={`col-12 mb24`}>
                                                    <DateTimePicker
                                                        customClassName={themeV3 ? 'mb24' : ''}
                                                        label="Date and Time"
                                                        id="matchStart"
                                                        field="eventStartUtc"
                                                        placeholderText="Select date"
                                                        minDate={this.props.club.tierType.isHighSchool ? moment().add('months', -3) : moment().add('years', -1)}
                                                        maxDate={null}
                                                        selected={this.state.dualMatchEventScheduleStartDate}
                                                        handleDateChange={(date) => this.handleDualMatchEventStartDateChange(date, formApi)}
                                                        validate={required}
                                                        themeV3={themeV3}
                                                        data-testid="event-edit.dual-match.match-start-date-picker"
                                                    />
                                                    {this.state.message && <div className="pb24 text-center text-error text-small">{this.state.message}</div>}
                                                    {
                                                        !themeV3 && (
                                                            <p className="text-small" data-testid="modal.edit-dual-match.p-209"><strong>Timezone: </strong>
                                                                {timeZones.find(t => t.id === this.state.timezone).label}
                                                                <a
                                                                    href="#"
                                                                    className="underline"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        this.toggleTimezone(true);
                                                                    }}
                                                                > change
                                                                </a>
                                                            </p>
                                                        )
                                                    }

                                                    {this.state.showTimezone
                                                        ? (
                                                            <Select
                                                                label={themeV3 ? `Time zone` : ''}
                                                                id="timezone"
                                                                hiddenLabel={true}
                                                                hiddenDefault={true}
                                                                field="timezone"
                                                                options={timeZones}
                                                                onChange={(val) => this.toggleTimezone(!!themeV3, val)}
                                                                themeV3={themeV3}
                                                                    data-testid="event-edit.dual-match.timezone-select"
                                                            />
                                                        )
                                                        : null}
                                                </div>
                                            </div>
                                            <div className="row mb16">
                                                <div className={`col-12 mb24`}>
                                                    <Select
                                                        key={`match-location`}
                                                        id={`match-location`}
                                                        label="Location"
                                                        field={`location`}
                                                        value={defaultValues.location}
                                                        onChange={(optionValue, e) => this.handleDualMatchLocationChange(optionValue, e, formApi)}
                                                        hiddenDefault={true}
                                                        options={this.props.dualMatchLocationOptions}
                                                        attributeToSave={`value`}
                                                        validate={required}
                                                        themeV3={themeV3}
                                                            data-testid="event-edit.dual-match.match-location-select"
                                                    />
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    )}
                                    {this.state.submitSuccessful && (
                                        <div className="row mb32 message">
                                            <div className="col-12 text-center">
                                                Dual match updated.
                                            </div>
                                        </div>
                                    )}
                                    {errors && (
                                        <div className="pb24 text-center text-error text-small" data-testid="modal.edit-dual-match.div-271">Both school and coach email are required fields.</div>
                                    )}
                                    {this.state.updateError && (
                                        <div className="pb24 text-center text-error text-small" data-testid="modal.edit-dual-match.div-274">
                                            An error occurred while updating this dual match. If this problem persists, please contact our <SupportLink target="_blank">support team.</SupportLink>
                                        </div>
                                    )}
                                    {this.state.submitSuccessful && (
                                        <div className={`row ${themeV3 ? styles.buttons : ''}`}>
                                            <div className="col-12">
                                                <button
                                                    onClick={onCloseClick}
                                                    type="button"
                                                    className="btn btn-default btn-xl text-uppercase btn-block"
                                                    data-testid="modal.edit-dual-match.got-it-btn"
                                                >
                                                    Got it
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {!this.state.submitSuccessful && (
                                        <div className={`row ${themeV3 ? styles.buttons : ''}`}>
                                            <div className="col-12 col-md-6 mb16">
                                                <button
                                                    onClick={onCloseClick}
                                                    type="button"
                                                    className="btn btn-default btn-xl text-uppercase btn-block"
                                                    data-testid="modal.edit-dual-match.cancel-btn"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-xl text-uppercase btn-block"
                                                    disabled={this.state.submitting}
                                                    data-testid="modal.edit-dual-match.update-btn"
                                                >
                                                    Update
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </form>
                            );
                        }}
                    </Form>
                </div>
            </Popup>
        );
    }
}

export default EditDualMatchEventPopup;
